import {
  WorkspaceDetailsDto,
  WorkspaceDocumentDto,
  WorkspaceDto,
  WorkspaceSearchModel,
} from 'src/app/modules/workspaces/models/workspaces.model';
import { COUNTERPARTY_SEARCH_CRITERION } from '../factories/counterparty-search-criterions.factory';
import { WorkspacesSearchCriterionsFactory } from '../factories/workspaces-search-criterions.factory';
import { SearchItemModel } from '../services/base-event-service';
import { FILTER_OPERATOR } from '../services/client-side-search.service';
import { WorkspaceCreateMetadata } from 'src/app/modules/workspaces/services/workspaces-api.service';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@ngneat/transloco';
import { NotificationsActions } from 'src/app/modules/notifications/store/actions';
import { WorkspacesActions } from 'src/app/modules/workspaces/store/actions';
import { constants } from '../constants/constants';

export function getWorkspaceById(id: string, workspaces: WorkspaceDto[]): WorkspaceDto | undefined {
  return workspaces.find((workspace) => workspace.id === id);
}

export function getWorkspacesFilterConditionsFromModel(
  model: WorkspaceSearchModel,
  workspacesSearchCriterionsFactory: WorkspacesSearchCriterionsFactory,
): SearchItemModel[] {
  let conditions: SearchItemModel[] = [];

  if (!!model.workspaceName) {
    const criterion = workspacesSearchCriterionsFactory.getById(COUNTERPARTY_SEARCH_CRITERION.NAME);
    const item = {
      criterion: criterion.id,
      operator: FILTER_OPERATOR.Contains,
      type: criterion.type,
      searchValue: model.workspaceName,
      controlName: criterion.controlName,
    } as SearchItemModel;

    conditions.push(item);
  }

  return conditions;
}

export async function prepareCreateWorkspacePayload(
  workspaceDocumentsForEdit: WorkspaceDocumentDto[],
  files: NgxFileDropEntry[],
): Promise<{
  metadata: Record<string, WorkspaceCreateMetadata>;
  filesById: Record<string, File>;
}> {
  const filesByRelativePath = await getFilesByRelativePath(files);
  const metadata: Record<string, WorkspaceCreateMetadata> = {};
  const filesById: Record<string, File> = {};

  workspaceDocumentsForEdit.forEach((workspaceDocument) => {
    const file = filesByRelativePath[workspaceDocument.id];
    const documentTypeId = workspaceDocument.type;
    const languageId = workspaceDocument.languageId;
    const positionId = workspaceDocument.userPositionId;

    if (
      !file ||
      documentTypeId === undefined ||
      languageId === undefined ||
      positionId === undefined
    ) {
      return;
    }

    const requestId = crypto.randomUUID();
    metadata[requestId] = {
      documentTypeId,
      languageId,
      positionId,
    };
    filesById[requestId] = file;
  });

  return { metadata, filesById };
}

export async function getFilesByRelativePath(
  files: NgxFileDropEntry[],
): Promise<Record<string, File>> {
  const fileEntries = await Promise.all(
    files.map(
      (droppedFile) =>
        new Promise<{ relativePath: string; file: File } | null>((resolve) => {
          if (!droppedFile.fileEntry.isFile) {
            resolve(null);
            return;
          }

          const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
          fileEntry.file((file: File) => {
            resolve({
              relativePath: droppedFile.relativePath,
              file,
            });
          });
        }),
    ),
  );

  return fileEntries.reduce(
    (accumulator, current) => {
      if (current) {
        accumulator[current.relativePath] = current.file;
      }

      return accumulator;
    },
    {} as Record<string, File>,
  );
}

export function sendWorkspaceCreationSuccessNotification(
  store: Store,
  translocoService: TranslocoService,
  workspace: WorkspaceDto,
) {
  const title = translocoService.translate('text.workspaceAdded', {
    name: workspace.name || '',
  });

  store.dispatch(
    NotificationsActions.showWorkspaceCreationNotificationComponent({
      icon: constants.icons.greenTick,
      title,
      duration: constants.notificationGenerateDocumentHideDelay,
      uniqueId: workspace.id,
    }),
  );
}

export function sendWorkspaceAddDocumentsSuccessNotification(
  store: Store,
  translocoService: TranslocoService,
  workspace: WorkspaceDto,
) {
  const title = translocoService.translate('text.workspaceUpdated', {
    name: workspace.name || '',
  });

  store.dispatch(
    NotificationsActions.showWorkspaceAddDocumentsNotificationComponent({
      icon: constants.icons.greenTick,
      title,
      duration: constants.notificationGenerateDocumentHideDelay,
      uniqueId: workspace.id,
    }),
  );
}

export function sendWorkspaceDocumentReviewFailedNotification(
  store: Store,
  translocoService: TranslocoService,
  dicId: string,
  workspace: WorkspaceDetailsDto,
) {
  const message = translocoService.translate('text.workspaceDocReviewFailed', {
    name: workspace.documents?.find((doc) => doc.id === dicId)?.name || '',
  });

  store.dispatch(
    NotificationsActions.showNotificationComponent({
      icon: constants.icons.redCross,
      title: translocoService.translate('text.reviewDocumentFailTitle'),
      duration: constants.notificationInfinityHideDelayKeyValue,
      message,
    }),
  );
}
